"use client";
import { useState } from "react";
import Head from "next/head";
import Link from "next/link";

/**
 * Partner-branded password reset page.
 * Submits to Cal.com's existing /api/auth/forgot-password endpoint so the
 * user gets a reset email. After they reset, they're directed back to the
 * partner portal login rather than the generic Cal.com login.
 */
export default function PartnerForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "11px 14px",
    border: "1.5px solid #e2e8f0",
    borderRadius: 10,
    fontSize: 14,
    color: "#111827",
    background: "#fff",
    outline: "none",
    fontFamily: "inherit",
    transition: "border-color 0.15s, box-shadow 0.15s",
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError("Please enter your email address.");
      setLoading(false);
      return;
    }

    try {
       // This keeps reset emails and password creation inside the Partner Portal
       // while maintaining the same no-account-enumeration behavior.
       const res = await fetch("/api/partner/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed }),
      });

      // Treat any non-5xx as success to avoid account enumeration.
      if (res.ok || res.status === 404 || res.status === 400) {
        setSent(true);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data?.message || "Something went wrong. Please try again.");
      }
    } catch {
      setError("Could not connect. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Head>
        <title>Reset Password — Schedly Partner Portal</title>
        <meta name="robots" content="noindex" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap"
          rel="stylesheet"
        />
        <style dangerouslySetInnerHTML={{ __html: `
          * { font-family: 'Inter', system-ui, sans-serif; box-sizing: border-box; }
          .blob { border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%; animation: blob-morph 8s ease-in-out infinite; }
          @keyframes blob-morph { 0%,100% { border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%; } 50% { border-radius: 50% 50% 33% 67% / 55% 27% 73% 45%; } }
          @keyframes spin { to { transform: rotate(360deg); } }
          .field-input:focus { border-color: #6366f1 !important; box-shadow: 0 0 0 3px rgba(99,102,241,0.15) !important; }
          .btn-reset { background: linear-gradient(135deg,#6366f1,#4f46e5); box-shadow: 0 4px 16px rgba(99,102,241,0.35); transition: all 0.2s ease; }
          .btn-reset:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(99,102,241,0.5); }
          .btn-reset:disabled { opacity: 0.65; cursor: not-allowed; }
          @media (max-width: 800px) { .portal-left { display: none !important; } }
        ` }} />
      </Head>

      <div style={{ minHeight: "100vh", display: "flex", fontFamily: "Inter,system-ui,sans-serif" }}>

        {/* ── LEFT PANEL ─────────────────────────────────────────────────────── */}
        <div
          className="portal-left"
          style={{ width: 440, flexShrink: 0, background: "linear-gradient(160deg,#0f0c29,#1a1040,#302b63)", position: "relative", overflow: "hidden", display: "flex", flexDirection: "column", padding: "52px 44px" }}>
          <div className="blob" style={{ position: "absolute", top: "10%", right: "-15%", width: 300, height: 300, background: "rgba(99,102,241,0.15)", filter: "blur(60px)" }} />
          <div className="blob" style={{ position: "absolute", bottom: "15%", left: "-10%", width: 240, height: 240, background: "rgba(124,58,237,0.12)", filter: "blur(50px)", animationDelay: "-4s" }} />

          <div style={{ position: "relative", zIndex: 1, flex: 1, display: "flex", flexDirection: "column" }}>
            <a href="https://schedly.io" style={{ display: "inline-block", marginBottom: 56 }}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/schedly-logo-white.png" alt="Schedly" style={{ height: 28, opacity: 0.95 }} />
            </a>

            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 12px", background: "rgba(99,102,241,0.2)", border: "1px solid rgba(165,180,252,0.25)", borderRadius: 20, color: "#a5b4fc", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", marginBottom: 16, width: "fit-content" }}>
              🔐 PARTNER PORTAL
            </span>

            <h1 style={{ color: "#fff", fontSize: 32, fontWeight: 900, letterSpacing: "-0.025em", lineHeight: 1.2, margin: "0 0 16px" }}>
              Reset your<br />password.
            </h1>
            <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 15, lineHeight: 1.65, margin: "0 0 48px" }}>
              Enter the email address on your partner account and we&apos;ll send you a reset link. You&apos;ll be back in the portal in minutes.
            </p>

            <div style={{ marginTop: "auto" }}>
              <div style={{ padding: 16, background: "rgba(255,255,255,0.04)", borderRadius: 12, border: "1px solid rgba(255,255,255,0.07)" }}>
                <div style={{ color: "rgba(255,255,255,0.35)", fontSize: 12, lineHeight: 1.6 }}>
                  Remember your password?{" "}
                  <a href="/partner/login" style={{ color: "#a5b4fc", textDecoration: "none", fontWeight: 600 }}>Sign in →</a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── RIGHT PANEL ────────────────────────────────────────────────────── */}
        <div style={{ flex: 1, background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center", padding: "48px 24px" }}>
          <div style={{ width: "100%", maxWidth: 420 }}>

            {/* Mobile logo */}
            <a href="https://schedly.io" style={{ display: "inline-block", marginBottom: 32 }}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/schedly-logo.png" alt="Schedly" style={{ height: 26, width: "auto" }} />
            </a>

            {sent ? (
              /* ── SUCCESS STATE ─────────────────────────────────────────────── */
              <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 18, padding: 32, boxShadow: "0 4px 20px rgba(0,0,0,0.04)", textAlign: "center" }}>
                <div style={{ width: 64, height: 64, borderRadius: 20, background: "linear-gradient(135deg,#6366f1,#7c3aed)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", boxShadow: "0 10px 28px rgba(99,102,241,0.35)" }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
                <h2 style={{ color: "#111827", fontSize: 22, fontWeight: 900, margin: "0 0 10px" }}>Check your inbox</h2>
                <p style={{ color: "#6b7280", fontSize: 14, lineHeight: 1.65, margin: "0 0 6px" }}>
                  If <strong style={{ color: "#111827" }}>{email}</strong> is linked to a partner account, you&apos;ll receive a reset link shortly.
                </p>
                <p style={{ color: "#9ca3af", fontSize: 13, lineHeight: 1.6, margin: "0 0 28px" }}>
                  After resetting your password, return here to sign in.
                </p>
                <Link
                  href="/partner/login"
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "11px 22px", background: "linear-gradient(135deg,#6366f1,#4f46e5)", color: "#fff", borderRadius: 10, fontSize: 14, fontWeight: 700, textDecoration: "none", boxShadow: "0 4px 14px rgba(99,102,241,0.35)" }}>
                  ← Back to Partner Login
                </Link>
              </div>
            ) : (
              /* ── FORM STATE ────────────────────────────────────────────────── */
              <>
                <h2 style={{ color: "#111827", fontSize: 26, fontWeight: 900, letterSpacing: "-0.02em", margin: "0 0 6px" }}>Forgot your password?</h2>
                <p style={{ color: "#6b7280", fontSize: 14, margin: "0 0 32px" }}>
                  Enter your partner account email and we&apos;ll send a reset link.
                </p>

                <form onSubmit={handleSubmit}>
                  <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 18, padding: 28, boxShadow: "0 4px 20px rgba(0,0,0,0.04)", marginBottom: 16 }}>

                    {error && (
                      <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "12px 14px", marginBottom: 20, display: "flex", gap: 10, alignItems: "flex-start" }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="8" cy="8" r="7" stroke="#ef4444" strokeWidth="1.5"/><path d="M8 5v3M8 10.5v.5" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round"/></svg>
                        <span style={{ color: "#b91c1c", fontSize: 13, lineHeight: 1.5 }}>{error}</span>
                      </div>
                    )}

                    <div style={{ marginBottom: 20 }}>
                      <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 6 }}>
                        Email address
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@company.com"
                        required
                        autoComplete="email"
                        autoFocus
                        className="field-input"
                        style={inputStyle}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-reset"
                      style={{ width: "100%", padding: "13px", borderRadius: 11, border: "none", color: "#fff", fontSize: 15, fontWeight: 800, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, letterSpacing: "-0.01em" }}>
                      {loading ? (
                        <>
                          <div style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Reset Link
                          <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        </>
                      )}
                    </button>
                  </div>
                </form>

                <p style={{ textAlign: "center", color: "#9ca3af", fontSize: 13 }}>
                  <Link href="/partner/login" style={{ color: "#4f46e5", fontWeight: 600, textDecoration: "none" }}>
                    ← Back to sign in
                  </Link>
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
